"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3 } from "lucide-react";
import { Component, type ReactNode } from "react";
import { formatUSPhoneNumber } from "@/shared/utils/phone-utils";
import { FileViewer } from "@/src/components/file-viewer/file-viewer";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";
import { useErrorLogs } from "@/src/lib/data-fetchers/error-log-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { buildOutboundFaxFilename, buildOutboundFaxGcsPath } from "@/src/lib/gcs-path";
import { replyFaxPdfOutputSchema, replyFaxRoutingOutputSchema } from "@/src/models/reply-fax";
import { TaskStatus } from "@/src/models/task";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
class FaxPdfPreviewBoundary extends Component<{
  children: ReactNode;
}, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  render() {
    return this.state.failed ? <p role="alert" className="p-4 text-sm">
        The fax PDF preview could not load. Refresh to try again.
      </p> : this.props.children;
  }
}

/** The submitted fax once sent, else the generated preview packet. */
export function outboundFaxPdfPath(taskId: string, taskSteps: TaskStep[]): string | null {
  const generated = taskSteps.find(step => step.stepKey === "build_reply_fax" && step.status === TaskStepStatus.COMPLETED);
  const packet = replyFaxPdfOutputSchema.safeParse(generated?.output);
  const submitted = taskSteps.some(step => step.status === TaskStepStatus.COMPLETED && step.output?.outcome === "submitted");
  return submitted ? buildOutboundFaxGcsPath(taskId, buildOutboundFaxFilename(taskId)) : packet.success ? packet.data.pathToFile : null;
}
export function OutboundFaxPdfPreview(t0) {
  const $ = _c(10);
  const {
    taskId,
    path
  } = t0;
  let t1;
  if ($[0] !== path) {
    t1 = fileViewUrl(path);
    $[0] = path;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== taskId) {
    t2 = buildOutboundFaxFilename(taskId);
    $[2] = taskId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <FileViewer url={t1} filename={t2} contentType="application/pdf" />;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== path || $[8] !== t3) {
    t4 = <FaxPdfPreviewBoundary key={path}>{t3}</FaxPdfPreviewBoundary>;
    $[7] = path;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
export type DocumentWorkflowStepDefinition = {
  key: string;
  title: string;
  description: string;
};
export function DocumentWorkflowSteps(t0) {
  const $ = _c(39);
  const {
    taskId,
    status,
    definitions,
    taskSteps
  } = t0;
  const t1 = status === TaskStatus.FAILED;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    errorLogs
  } = useErrorLogs(taskId, t2);
  let t3;
  if ($[2] !== errorLogs) {
    t3 = errorLogs.toSorted(_temp)[0]?.errorMessage;
    $[2] = errorLogs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const latestError = t3;
  let t4;
  if ($[4] !== definitions || $[5] !== taskSteps) {
    let t5;
    if ($[7] !== taskSteps) {
      t5 = definition => taskSteps.find(step => step.stepKey === definition.key)?.status !== TaskStepStatus.COMPLETED;
      $[7] = taskSteps;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    t4 = definitions.findIndex(t5);
    $[4] = definitions;
    $[5] = taskSteps;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const firstUnfinished = t4;
  let t5;
  if ($[9] !== taskSteps) {
    const generated = taskSteps.find(_temp2);
    t5 = replyFaxPdfOutputSchema.safeParse(generated?.output);
    $[9] = taskSteps;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const packet = t5;
  let t6;
  if ($[11] !== taskId || $[12] !== taskSteps) {
    t6 = outboundFaxPdfPath(taskId, taskSteps);
    $[11] = taskId;
    $[12] = taskSteps;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const pdfPath = t6;
  let t7;
  if ($[14] !== status) {
    t7 = status === TaskStatus.AWAITING_INFORMATION && <p className="text-sm text-muted-foreground">Awaiting an updated document. Fax submission does not confirm delivery.</p>;
    $[14] = status;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== definitions || $[17] !== firstUnfinished || $[18] !== latestError || $[19] !== packet || $[20] !== status || $[21] !== taskSteps) {
    let t9;
    if ($[23] !== firstUnfinished || $[24] !== latestError || $[25] !== packet || $[26] !== status || $[27] !== taskSteps) {
      t9 = (definition_0, index) => {
        const step_1 = taskSteps.find(candidate => candidate.stepKey === definition_0.key);
        const failed = step_1?.status === TaskStepStatus.FAILED;
        const completed = step_1?.status === TaskStepStatus.COMPLETED;
        const skipped = completed && step_1.output?.outcome === "skipped";
        const waiting = step_1?.status === TaskStepStatus.ASYNC || step_1?.status === TaskStepStatus.PAUSED;
        const running = !step_1 && status === TaskStatus.PROCESSING && index === firstUnfinished;
        const label = failed ? "Failed" : skipped ? "Not needed" : completed ? step_1.output?.outcome === "submitted" ? "Submitted" : "Completed" : waiting ? "Waiting" : running ? "In progress" : "Not run";
        const routing = replyFaxRoutingOutputSchema.safeParse(step_1?.output);
        return <ValerieCard key={definition_0.key} title={`${index + 1}. ${definition_0.title}`} icon={failed ? <CircleAlert /> : completed ? <CheckCircle2 /> : waiting || running ? <Clock3 /> : <CircleDashed />} variant={failed ? "destructive" : completed && !skipped ? "success" : "outline"} actions={<Badge variant="outline">{label}</Badge>} contentClassName="space-y-2 pt-4"><p className="text-sm text-muted-foreground">{definition_0.description}</p>{routing.success && <p className="text-sm">Fax to: {formatUSPhoneNumber(routing.data.targetFaxNumber)}</p>}{definition_0.key === "build_reply_fax" && packet.success && <p className="text-sm">{packet.data.pageCount} pages generated. Preview below.</p>}{failed && <p role="alert" className="text-sm text-destructive">{latestError || "This step failed. Later steps have not run."}</p>}</ValerieCard>;
      };
      $[23] = firstUnfinished;
      $[24] = latestError;
      $[25] = packet;
      $[26] = status;
      $[27] = taskSteps;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    t8 = definitions.map(t9);
    $[16] = definitions;
    $[17] = firstUnfinished;
    $[18] = latestError;
    $[19] = packet;
    $[20] = status;
    $[21] = taskSteps;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[29] !== definitions.length) {
    t9 = definitions.length === 0 && <p className="text-sm text-muted-foreground">No workflow steps are configured for this task.</p>;
    $[29] = definitions.length;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== pdfPath || $[32] !== taskId) {
    t10 = pdfPath && <ValerieCard title="Fax PDF" contentClassName="h-[900px] p-0"><OutboundFaxPdfPreview taskId={taskId} path={pdfPath} /></ValerieCard>;
    $[31] = pdfPath;
    $[32] = taskId;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t11 = <div className="space-y-4">{t7}{t8}{t9}{t10}</div>;
    $[34] = t10;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
}
function _temp2(step_0) {
  return step_0.stepKey === "build_reply_fax" && step_0.status === TaskStepStatus.COMPLETED;
}
function _temp(a, b) {
  return b.createdAt.localeCompare(a.createdAt);
}